<template>
  <div class="content-info-page-header">
    <div class="content-info-page-header__content">
      <div class="content-info-page-header__text">
        <SHeading level="1" size="1">{{ title }}</SHeading>
        <ContentRichText
          v-if="description"
          class="content-info-page-header__description"
          :content="description"
        />
      </div>

      <div v-if="priceAverage" class="content-info-page-header__graph">
        <span class="content-info-page-header__price-avg">
          {{ priceAverage }}
        </span>

        <div class="content-info-page-header__curve">
          <img
            alt=""
            class="content-info-page-header__curve-img"
            :src="bellCurveImage"
          />
          <div class="content-info-page-header__price-min">
            <span class="content-info-page-header__price-label">Minimum</span>
            <span class="content-info-page-header__price-value">
              {{ priceMinimum }}
            </span>
          </div>
          <div class="content-info-page-header__price-max">
            <span class="content-info-page-header__price-label">Maximum</span>
            <span class="content-info-page-header__price-value">
              {{ priceMaximum }}
            </span>
          </div>
        </div>
      </div>

      <PCard
        v-if="stats.length > 0"
        class="content-info-page-header__stats"
        :pt="{ content: 'content-info-page-header__stats__content' }"
      >
        <div
          v-for="stat in stats"
          :key="stat.id"
          class="content-info-page-header__stat"
        >
          <ContentIcon
            class="content-info-page-header__stat-icon"
            :icon="stat.icon"
          />
          <div>
            <span class="content-info-page-header__stat-value">
              {{ stat.value }}
            </span>
            <span class="content-info-page-header__stat-label">
              {{ stat.label }}
            </span>
          </div>
        </div>
      </PCard>

      <ContentCtaForm
        v-if="formUrl"
        class="content-info-page-header__form"
        :form-submit-url="formUrl"
        hide-label
        show-usps
      />

      <ContentPageMeta
        :author="author"
        class="content-info-page-header__meta content-info-page-header__meta--mobile"
        :last-update="lastUpdate"
      />
    </div>

    <ContentPageMeta
      :author="author"
      class="content-info-page-header__meta content-info-page-header__meta--desktop"
      :last-update="lastUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import { PCard, SHeading } from "@solvari/components";

import bellCurveImage from "~/shared/assets/bell-curve.svg";
import ContentCtaForm from "~/shared/components/content-blocks/ContentCtaForm.vue";
import ContentRichText from "~/shared/components/content-blocks/ContentRichText.vue";
import ContentIcon from "~/shared/components/content-blocks/internal/ContentIcon.vue";
import ContentPageMeta from "~/shared/components/content-blocks/internal/ContentPageMeta.vue";

type StatItem = {
  icon: string | null;
  id: string;
  label: string | null;
  value: string | null;
};

withDefaults(
  defineProps<{
    author?: string | null;
    description: string | null;
    formUrl?: string | null;
    lastUpdate?: string | null;
    priceAverage?: string | null;
    priceMaximum?: string | null;
    priceMinimum?: string | null;
    stats?: StatItem[];
    title: string;
  }>(),
  {
    author: null,
    formUrl: null,
    lastUpdate: null,
    priceAverage: null,
    priceMaximum: null,
    priceMinimum: null,
    stats: () => [],
  },
);
</script>

<style lang="postcss">
.content-info-page-header {
  container-type: inline-size;

  &__content {
    @apply mx-auto grid w-full max-w-[1040px] pb-6 text-center gap-y-6;
    grid-template:
      "text"
      "meta"
      "graph"
      "stats"
      "form";
  }

  &__text {
    @apply mx-auto flex w-full max-w-[896px] flex-col gap-y-4 area-[text];

    & .s-heading-1 {
      @apply mb-0;
    }
  }

  &__description {
    @apply text-base;
  }

  &__meta {
    &--mobile {
      @apply -mt-4 area-[meta];

      @container (min-width: 600px) {
        @apply hidden;
      }
    }

    &--desktop {
      @apply hidden;

      @container (min-width: 600px) {
        @apply ml-auto flex w-full max-w-[1040px] justify-end pb-6;
      }
    }
  }

  &__graph {
    @apply mx-auto flex w-full max-w-[700px] flex-col area-[graph];
  }

  &__price {
    &-avg {
      @apply self-center rounded-lg bg-primary-700 px-4 py-1 text-sm font-bold text-white;
    }

    &-min {
      @apply absolute bottom-4 left-0 flex flex-col;
    }

    &-max {
      @apply absolute bottom-4 right-0 flex flex-col text-right;
    }

    &-label {
      @apply text-xs text-slate-500;
    }

    &-value {
      @apply text-sm font-bold text-primary-600;
    }
  }

  &__curve {
    @apply relative mt-2 w-full;

    &-img {
      @apply w-full;
    }
  }

  &__stats {
    @apply mx-auto w-full max-w-[700px] area-[stats];

    &__content {
      @apply grid grid-cols-3 gap-2;
    }
  }

  &__stat {
    @apply flex items-center justify-center text-center gap-2;

    &-icon {
      @apply hidden;
    }

    &-value {
      @apply block text-sm font-bold text-primary-900;
    }

    &-label {
      @apply block text-xs text-slate-500;
    }
  }

  &__form {
    @apply mx-auto w-full max-w-[700px] area-[form];
  }

  @container (min-width: 600px) {
    &__content {
      grid-template:
        "text"
        "graph"
        "stats"
        "form";
    }

    &__description {
      @apply text-lg;
    }

    &__price {
      &-avg,
      &-value {
        @apply text-base;
      }
    }

    &__stat {
      @apply text-left;

      &-icon {
        @apply flex;
      }

      &-value {
        @apply text-base;
      }
    }
  }
}
</style>
